import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    useChartOfAccounts,
    useCurrentTaxSettingsQuery,
    useOperationHistoryQuery,
} from '@/api/queries';
import {
    isoToDisplayDate,
    resolveLocale,
} from '@/pages/accounting/statements/statementMeta';
import { postableAccounts } from '../../editor/operationEditorMeta';
import {
    latestPublishedVersion,
    toPreviewPairs,
    type TemplatePreview,
} from './previewModalMeta';

type UsePreviewModalOptions = {
    /** The template to preview; `null` closes the modal and fetches nothing. */
    operationKey: string | null;
};

export type UsePreviewModalResult = {
    /** The version on screen, or `null` while loading, on failure, or when there is none. */
    preview: TemplatePreview | null;
    isLoading: boolean;
    /** A failed read. Rendered inside the modal, never thrown. */
    hasError: boolean;
    /** Loaded, and this template has never published a version. */
    isEmpty: boolean;
};

/**
 * Drives the catalog's posting-template preview.
 *
 * **Read from the history, not from the template detail.** `GET
 * /ledger/operations/{key}` carries the lines of the version in force *today*
 * and of the draft; the modal shows the **latest published** version, which is a
 * third thing whenever a change is scheduled for next month. The history
 * endpoint is the only one that carries every version's full line set with a
 * status that can tell "applies today" from "will apply".
 *
 * The chart comes with it because a template line stores an account **id** —
 * without the chart every fixed line would render as a uuid — and the tax
 * settings because a VAT pair's fraction is the tenant's rate in force, never a
 * constant in this file.
 *
 * **A failed read is returned, not thrown.** The catalog behind this modal is
 * working; throwing would replace it with a full-page error because one row
 * could not be explained. Same rule as the Activity detail drawer.
 */
export const usePreviewModal = ({
    operationKey,
}: UsePreviewModalOptions): UsePreviewModalResult => {
    const { i18n } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);
    const isOpen = operationKey !== null;

    const history = useOperationHistoryQuery(operationKey ?? '', isOpen);
    const chart = useChartOfAccounts({ locale, enabled: isOpen });
    const taxes = useCurrentTaxSettingsQuery(isOpen);

    const template = history.data?.success ? history.data.response : null;
    const accounts = chart.data;

    // The postable leaves, sorted by code — the same set the editor resolves a
    // line against, so a slot's candidate codes read identically in both.
    const postable = React.useMemo(
        () => (accounts ? postableAccounts(accounts) : []),
        [accounts],
    );

    const version = React.useMemo(
        () => (template ? latestPublishedVersion(template.versions) : null),
        [template],
    );

    // The rate is a documented 404 for a tenant with none set in force, so a
    // failed read is zero rather than an error state — the same reading the
    // editor's preview takes.
    const vatRateBps = taxes.data?.success
        ? taxes.data.response.rates.vatStandardBps
        : 0;

    const preview = React.useMemo<TemplatePreview | null>(() => {
        if (!version || !accounts) {
            return null;
        }
        return {
            versionId: version.id,
            version: version.version,
            status: version.status,
            effectiveFromText: isoToDisplayDate(version.effectiveFrom),
            pairs: toPreviewPairs(version.lines, postable, vatRateBps),
        };
    }, [version, accounts, postable, vatRateBps]);

    const hasError =
        isOpen &&
        ((history.data ? !history.data.success : Boolean(history.error)) ||
            chart.error !== null);

    const isLoading =
        isOpen && !hasError && (template === null || accounts === null);

    return {
        preview,
        isLoading,
        hasError,
        isEmpty:
            !isLoading && !hasError && template !== null && version === null,
    };
};
